import { TypedUseSelectorHook, useSelector } from 'react-redux'

import type { RootState } from '..'
import type { Session, User, UserState } from '../types'

// cannot use the one from index.tsx here because of circular dependency issues with jest
const useTypedSelector: TypedUseSelectorHook<RootState> = useSelector

export const maintenanceUrlPatterns = [
  /\/login*/,
  /\/towing*/,
  /\/search\/towings/,
  /\/options*/,
  /\/about*/,
]

type UserRoles = Omit<
  User,
  'number' | 'originalNumber' | 'name' | 'familyName' | 'personnelGroup' | 'error' | 'loading'
>
export type Role = keyof UserRoles

export const adminRoleKey = 'admin'
export const readAdminRoleKey = 'read_admin'

export const properNonAdminRoleKeys = [
  'commuter_driver',
  'driver',
  'commuter_manager',
  'conductor',
  'commuter_conductor',
  'other',
  'logistics_driver',
  'maintenance',
  'waiter',
  'commuter_security',
  'bus_driver',
  'bus_driver_manager',
] as const

const hasAdminAssumedRole = (user: User, assumedRole: Role) => {
  return (user.admin || user.read_admin) && user[assumedRole]
}

export const isMaintenanceUser = (user: User) =>
  user.maintenance ||
  hasAdminAssumedRole(user, 'maintenance') ||
  hasAdminAssumedRole(user, 'actAsMaintenance')

export const isWithinMaintenanceUrls = (pathName: string) =>
  maintenanceUrlPatterns.some((pattern) => pattern.test(pathName))

export const canUseBusDriverShiftSignIn = (user: User) =>
  user.bus_driver || user.bus_driver_manager || hasAdminAssumedRole(user, 'bus_driver')

export type DriverType = 'commuter_driver' | 'driver' | 'logistics_driver'

export const checkUserForDriverRole = (user: User): DriverType | null => {
  if (user.commuter_driver || (user.commuter_manager && user.number !== user.originalNumber)) {
    return 'commuter_driver'
  }
  if (user.driver) {
    return 'driver'
  }
  if (user.logistics_driver) {
    return 'logistics_driver'
  }
  return null
}

export const isESalliUser = (session: Session) => {
  return (
    session.conductor ||
    session.driver ||
    session.logistics_driver ||
    session.waiter ||
    session.maintenance ||
    session.other
  )
}

export const isCommuterUser = (session: Session | User) => {
  return (
    session.commuter_driver ||
    session.commuter_manager ||
    session.commuter_conductor ||
    session.commuter_security
  )
}

export const isOriginalAdmin = (session: Session | User) => {
  return session.originalAdmin || false
}

export const hideSchedule = (session: Session | User) => {
  return (
    session.commuter_security || isMaintenanceUser(session) || canUseBusDriverShiftSignIn(session)
  )
}

export const hideCalendarLink = (session: Session | User) => {
  return (
    session.commuter_security || canUseBusDriverShiftSignIn(session) || isMaintenanceUser(session)
  )
}

/** NOTE: always allows for admin and read_admin */
export const createRoleSelector = <T extends keyof UserState>(...roles: T[]) => {
  return (state: { user: UserState }) =>
    ([...roles, 'admin', 'read_admin'] as const).some((role) => state.user[role])
}

const defaultSelector = () => true

export const AllowedSelectorWrapper = ({
  selector,
  children,
}: {
  selector?: (state: RootState) => boolean
  children: JSX.Element | null
}) => {
  const isAllowed = useTypedSelector(selector ?? defaultSelector)
  return isAllowed ? children : null
}

// NOTE: when updating, also update pushRoles in backend
export const pushAllowedSelector = createRoleSelector(
  'commuter_driver',
  'commuter_conductor',
  'conductor',
  'driver',
  'logistics_driver'
)

export const allowTimetableFetchSelector = createRoleSelector(
  'commuter_driver',
  'commuter_manager',
  'driver',
  'logistics_driver'
)

export const allowCompositionsSelector = createRoleSelector(
  'driver',
  'conductor',
  'waiter',
  'logistics_driver',
  'maintenance',
  'commuter_driver',
  'other'
)
